import {
  useActionEngineState,
  useApp,
  useChangeGroup,
  useDealerTheme,
  useTypedSelector,
} from '@k3/core'
import { Text } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { Ref, useEffect, useMemo, useRef, useState } from 'react'
import {
  MathUtils,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Shape,
} from 'three'
import { degToRad } from 'three/src/math/MathUtils'
import { useVerandaSelector } from '../../../..'
import { useMeasures } from '../../../../hooks/state/useMeasures'
import { useSidewall } from '../../../../hooks/state/useSidewall'
import { Keys } from '../../../../hooks/state/utlility/utils'

const extrudeSettings = {
  curveSegments: 1,
  steps: 1,
  depth: 0.01,
  bevelEnabled: false,
}

interface HandlePointerEvent extends THREE.Event {
  stopPropagation: () => void
}

export const SidewallHighlight = () => {
  const materialRef = useRef<MeshBasicMaterial>()
  const materialRefSq = useRef<MeshBasicMaterial>()
  const { invalidate } = useThree()
  const [isPointOver, setIsPointOver] = useState(false)

  const {
    spaceBetween,
    sidewallId,
    isWallCenter,
    type,
    sideWall,
    sideWallVariable,
    spaceBetweenY,
  } = useSidewall()
  const { userHeight, frontHeight, attachment, verandaTyp, userAngle } =
    useMeasures()
  const [isPointerDown, setIsPointerDown] = useState(false)
  const [pointerDownTime, setPointerDownTime] = useState<number | null>(null)
  const doubleClickTime = 300
  const changeGroup = useChangeGroup()
  const app = useApp()
  const highlight = useVerandaSelector((state) =>
    state.highlights.highlights.find((h) => h.id === sidewallId)
  )

  const isRightSide = type === Keys.Seitenwände.Values.SeitenwandRechts.key
  const wallHeight = spaceBetweenY

  const primaryColor = app.settings.ext?.theme?.palette?.primary.main as string
  const dealerPrimaryColor = useDealerTheme()?.palette?.primary?.main

  const shape = useMemo(() => {
    const shape = new Shape()

    if (isWallCenter) {
      shape
        .moveTo(-spaceBetween / 2, 0)
        .lineTo(-spaceBetween / 2, wallHeight)
        .lineTo(spaceBetween / 2, wallHeight)
        .lineTo(spaceBetween / 2, 0)
    } else {
      shape
        .moveTo(-spaceBetween / 2, 0)
        .lineTo(
          -spaceBetween / 2,
          userHeight - Math.tan((userAngle * Math.PI) / 180) * spaceBetween
        )
        .lineTo(spaceBetween / 2, userHeight)
        .lineTo(spaceBetween / 2, 0)
    }
    return shape
  }, [spaceBetween, userHeight, frontHeight])

  useEffect(() => {
    invalidate()
  }, [highlight, isPointOver, sideWall.noSelection])

  useEffect(() => {
    if (!materialRef.current) return
    materialRef.current.opacity = 0
  }, [materialRef.current])

  const { concealVariables, hideVariables } = useActionEngineState() ?? {}
  const isAlwaysHidden = sideWallVariable?.settings?.templateOptions?.hideAlways
  const isConcealed = concealVariables?.some(
    (concealAction) => concealAction.variableId === sideWallVariable?.id
  )
  const isHidden = hideVariables?.some(
    (hideAction) => hideAction.variableId === sideWallVariable?.id
  )
  const isActive = !isAlwaysHidden && !isConcealed && !isHidden

  const goal = highlight || isPointOver ? 0.5 : 0
  const isVisible =
    isPointOver ||
    !!(
      materialRef?.current &&
      (Math.abs(goal - materialRef.current?.opacity) >= 0.01 || goal > 0)
    )

  useFrame((_state, delta) => {
    if (delta > 0.1) {
      invalidate()
      return
    }

    if (!materialRef.current) return

    materialRef.current.opacity = MathUtils.damp(
      materialRef.current?.opacity ?? 0,
      goal,
      15,
      delta
    )

    if (Math.abs(goal - materialRef.current?.opacity) >= 0.01 && isVisible) {
      invalidate()
    } else {
      materialRef.current.opacity = goal
    }
  })

  const startPos = isWallCenter
    ? verandaTyp.log.width / 2
    : verandaTyp.log.depth / 2 + (attachment.free ? verandaTyp.log.depth : 0)

  const handlePointer = (event: HandlePointerEvent, state: boolean): void => {
    event.stopPropagation()

    if (materialRef.current) {
      materialRef.current.opacity = state ? 0.5 : 0
      setIsPointOver(state)
    }
  }
  const handlePointerDown = (event: HandlePointerEvent): void => {
    event.stopPropagation()
    setIsPointerDown(true)
    setPointerDownTime(Date.now())
  }

  const handlePointerUp = (event: HandlePointerEvent): void => {
    event.stopPropagation()

    if (isPointerDown) {
      const pressDuration = Date.now() - (pointerDownTime ?? 0)
      if (pressDuration > doubleClickTime) {
        return
      } else {
        if (!sideWallVariable?.settings?.templateOptions?.hideAlways) {
          changeGroup(sidewallId)
        }
      }
    }

    setIsPointerDown(false)
    setPointerDownTime(null)
  }

  useEffect(() => {
    invalidate()
  }, [isVisible])

  const show = isActive && isVisible

  // return null
  return (
    <group
      visible={show}
      position={[0, 0, startPos]}
      rotation={[0, degToRad(90), 0]}
      onPointerMove={(e) => handlePointer(e, true)}
      onPointerOut={(e) => handlePointer(e, false)}
      onPointerDown={show ? handlePointerDown : undefined}
      onPointerUp={show ? handlePointerUp : undefined}
    >
      <mesh>
        <extrudeGeometry attach="geometry" args={[shape, extrudeSettings]} />
        <meshBasicMaterial
          ref={materialRef as Ref<MeshBasicMaterial>}
          color={dealerPrimaryColor || primaryColor}
          transparent
          depthTest={false}
          opacity={materialRef?.current?.opacity ?? 0}
        />
        <group
          position={[0, 0, isRightSide ? 0.05 : -0.05]}
          rotation={[0, isRightSide ? 0 : -Math.PI, 0]}
          visible={sideWall.noSelection && (isPointOver || !!highlight)}
        >
          <mesh position={[0, wallHeight / 2 + 0.1, 0]} scale={[0.1, 0.1, 0.1]}>
            <circleGeometry args={[0.8, 32]} />
            <meshBasicMaterial
              ref={materialRefSq as Ref<MeshBasicMaterial>}
              transparent
              depthTest={false}
              opacity={materialRef?.current?.opacity ?? 0}
            />
            <Text color="black" fontSize={1} position={[0, -0.05, 0]}>
              +
            </Text>
          </mesh>
        </group>
      </mesh>
    </group>
  )
}
